/**
 * `src/lib/ui/nav/skip.ts` — a skip link that does not add a history entry.
 *
 * A plain `<a href="#main">` is a same-page fragment link, and SvelteKit lets
 * the browser handle it: the browser PUSHES `/qibla#main`, and Kit bumps its
 * history index without a navigation, so `afterNavigate` never fires and the
 * navigation's history mirror never sees the entry. The app bar's back control
 * is the very next tab stop, and in history mode it is `history.back()`, which
 * then only strips the `#main` and appears to do nothing (Android's system back
 * did the same).
 *
 * So the click is taken over: the target is scrolled to and focused, which is
 * everything the fragment did for a keyboard or switch user, and the URL and
 * the history stay as they were. A modified or non-primary click is left alone.
 * It must be a Svelte `onclick`: Svelte's delegated handler runs before Kit's
 * link listener on <html>, which skips an event that is already
 * `defaultPrevented` (`BackButton` relies on the same order).
 */
export function skipToTarget(event: MouseEvent): void {
	if (event.defaultPrevented || event.button !== 0) return;
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
	const link = event.currentTarget;
	if (!(link instanceof HTMLAnchorElement)) return;
	const id = decodeURIComponent(link.hash.slice(1));
	const target = id ? document.getElementById(id) : null;
	if (!target) return;
	event.preventDefault();
	// `scrollIntoView` honours the root's `scroll-padding-block-start`, so the
	// target lands below the sticky app bar, as the fragment did.
	target.scrollIntoView({ block: 'start' });
	target.focus({ preventScroll: true });
}
